import React, { Component, ErrorInfo, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { WarningIcon } from '@phosphor-icons/react';
import { T } from 'gt-react';
import { Card } from '@/components/ui/card';
import { StaleChunkRecovery } from '@/components/common/StaleChunkRecovery';
import { isQuietStaleChunk } from '@/lib/stale-chunk-error';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // A missing hashed chunk means this tab is older than the deploy. The
    // refresh prompt is the recovery; it is not a bug to report. A caller
    // that passed its own fallback (the login background) already recovered.
    if (isQuietStaleChunk(error)) {
      return;
    }

    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
    });

    // Report the error to Sentry
    Sentry.withScope((scope) => {
      scope.setTag('source', 'react-error-boundary');
      scope.setContext('react', {
        componentStack: errorInfo.componentStack,
      });
      scope.setLevel('error');
      Sentry.captureException(error);
    });
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (isQuietStaleChunk(this.state.error)) {
        return <StaleChunkRecovery />;
      }

      // Modern fallback UI with improved design
      return (
        <div className="max-w-2xl w-full mx-auto flex justify-center items-center min-h-[400px]">
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <div className="p-8">
              <div className="flex items-center space-x-4 mb-6">
                <div className="flex-shrink-0">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
                    <WarningIcon className="h-6 w-6 text-red-600" />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-1">
                    <T>Oops! Something went wrong</T>
                  </h3>
                  <p className="text-gray-600">
                    <T>We encountered an unexpected error</T>
                  </p>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <p className="text-sm text-gray-700 leading-relaxed">
                  {`Don't worry — this has been automatically reported to our
                  team. Try refreshing the page, or contact support if the
                  problem persists.`}
                </p>
              </div>

              {/* Show error details in development */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <details className="mt-6">
                  <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900 mb-3 select-none">
                    <T>🔧 Development Error Details</T>
                  </summary>
                  <div className="bg-gray-900 rounded-lg p-4 border">
                    <pre className="text-xs text-green-400 whitespace-pre-wrap break-words max-h-40 overflow-auto font-mono leading-relaxed">
                      {this.state.error.toString()}
                      {this.state.errorInfo?.componentStack}
                    </pre>
                  </div>
                </details>
              )}
            </div>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
